import React, { useRef, useLayoutEffect, useEffect } from "react";

export interface TabOption {
  value: string;
  label?: string;
  icon?: React.ReactNode;
  ariaLabel?: string;
  title?: string;
}

interface TabsProps {
  value: string;
  onChange: (value: string) => void;
  options: TabOption[];
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  value,
  onChange,
  options,
  className = ""
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = React.useState({ 
    transform: "translateX(0px)", 
    width: "0px" 
  });

  // Update indicator position
  useLayoutEffect(() => {
    const activeIndex = options.findIndex(opt => opt.value === value);
    if (activeIndex === -1) return;
    
    const tab = tabRefs.current[activeIndex];
    const container = containerRef.current;
    
    if (tab && container) {
      const containerRect = container.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const left = tabRect.left - containerRect.left;
      const width = tabRect.width;
      
      setIndicatorStyle({
        transform: `translateX(${left}px)`,
        width: `${width}px`
      });
    }
  }, [value, options]);

  // Also update on resize
  useEffect(() => {
    const handleResize = () => {
      const activeIndex = options.findIndex(opt => opt.value === value);
      if (activeIndex === -1) return;
      
      const tab = tabRefs.current[activeIndex];
      const container = containerRef.current;
      
      if (tab && container) {
        const containerRect = container.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        const left = tabRect.left - containerRect.left;
        const width = tabRect.width;
        
        setIndicatorStyle({
          transform: `translateX(${left}px)`,
          width: `${width}px`
        });
      }
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [value, options]);

  return (
    <div ref={containerRef} className={`ob-tabs ${className}`}>
      <div className="ob-tabs-indicator" style={indicatorStyle} />
      {options.map((option, index) => (
        <button
          key={option.value}
          ref={(el) => { tabRefs.current[index] = el; }}
          type="button"
          className={`ob-tab ${value === option.value ? "ob-tab-active" : ""}`}
          onClick={() => onChange(option.value)}
          aria-label={option.ariaLabel || option.label}
          title={option.title || option.label}
        >
          {option.icon && <span className="ob-tab-icon">{option.icon}</span>}
          {option.label && <span className="ob-tab-label">{option.label}</span>}
        </button>
      ))}
    </div>
  );
};
